import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Library,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  ChevronRight,
  CheckCircle,
  Clock,
  Archive,
  Play,
  Pause,
  RefreshCw,
  Save,
  Link2,
  Zap,
  Target,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
  inactive: { label: 'Inactive', color: 'bg-slate-500/10 text-slate-400 border-slate-500/30' },
  draft: { label: 'Draft', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  archived: { label: 'Archived', color: 'bg-slate-600/10 text-slate-500 border-slate-600/30' },
};

const TRIGGER_TYPES = [
  { value: 'account_type', label: 'Account Type' },
  { value: 'dispute_type', label: 'Dispute Type' },
  { value: 'finding_type', label: 'Finding Type' },
  { value: 'confidence_level', label: 'Confidence Level' },
  { value: 'provider', label: 'Provider' },
  { value: 'manual', label: 'Manual' },
];

const MethodCard = ({ method, onEdit, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div 
      className="bg-stone-900 border border-stone-800 rounded-xl p-5 hover:border-amber-500/30 transition-all"
      data-testid={`method-card-${method.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-600/20 flex items-center justify-center">
            <Library className="text-emerald-400" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-white">{method.name}</h3>
            <p className="text-xs text-slate-500 font-mono">{method.slug}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 text-xs rounded-full border ${STATUS_CONFIG[method.status]?.color}`}>
            {STATUS_CONFIG[method.status]?.label || method.status}
          </span>
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-stone-800 transition-all"
            >
              <MoreVertical size={16} />
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-1 w-36 bg-stone-800 border border-slate-700 rounded-lg shadow-xl z-10 py-1">
                <button
                  onClick={() => { onEdit(method); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  <Edit size={14} /> Edit
                </button>
                <button
                  onClick={() => { onDelete(method); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-slate-700"
                >
                  <Trash2 size={14} /> Archive
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Purpose */}
      <p className="text-sm text-slate-400 mb-4 line-clamp-2">{method.purpose || method.description}</p>

      {/* Trigger & Tiers */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-stone-800/50 rounded-lg p-2">
          <p className="text-xs text-slate-500 mb-1">Trigger Type</p>
          <div className="flex items-center gap-1">
            <Zap size={12} className="text-amber-400" />
            <span className="text-sm text-slate-300 capitalize">{method.trigger_type?.replace('_', ' ')}</span>
          </div>
        </div>
        <div className="bg-stone-800/50 rounded-lg p-2">
          <p className="text-xs text-slate-500 mb-1">Order</p>
          <span className="text-sm text-slate-300">{method.execution_order || 100}</span>
        </div>
      </div>

      {/* Tiers */}
      <div className="flex flex-wrap gap-1 mb-3">
        {(method.allowed_tiers || []).map(tier => (
          <span key={tier} className="px-2 py-0.5 text-xs bg-amber-500/10 text-amber-300 rounded">
            {tier.replace('_', ' ')}
          </span>
        ))}
      </div>

      {/* Linked Items */}
      <div className="flex items-center justify-between pt-3 border-t border-stone-800">
        <div className="flex items-center gap-3 text-xs text-slate-500">
          {method.linked_prompt_id && (
            <span className="flex items-center gap-1">
              <Link2 size={12} /> Prompt
            </span>
          )}
          {method.linked_template_id && (
            <span className="flex items-center gap-1">
              <Link2 size={12} /> Template
            </span>
          )}
        </div>
        {method.usage_count > 0 && (
          <span className="text-xs text-slate-500">{method.usage_count} uses</span>
        )}
      </div>
    </div>
  );
};

const MethodEditor = ({ method, onSave, onClose, isNew }) => {
  const [formData, setFormData] = useState({
    name: method?.name || '',
    slug: method?.slug || '',
    description: method?.description || '',
    purpose: method?.purpose || '',
    round: method?.round || 1,
    strategy_type: method?.strategy_type || 'structural',
    applies_when: method?.applies_when || '',
    exclude_when: method?.exclude_when || '',
    law_basis: method?.law_basis || '',
    trigger_type: method?.trigger_type || 'manual',
    trigger_conditions: method?.trigger_conditions || {},
    input_requirements: method?.input_requirements || [],
    allowed_tiers: method?.allowed_tiers || ['auto_starter', 'specialist_pro', 'agency'],
    status: method?.status || 'draft',
    execution_order: method?.execution_order || 100,
    linked_prompt_id: method?.linked_prompt_id || '',
    linked_template_id: method?.linked_template_id || '',
    fallback_method_id: method?.fallback_method_id || '',
    source_lesson: method?.source_lesson || '',
  });
  const [saving, setSaving] = useState(false);

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: isNew ? generateSlug(name) : prev.slug,
    }));
  };

  const toggleTier = (tier) => {
    setFormData(prev => ({
      ...prev,
      allowed_tiers: prev.allowed_tiers.includes(tier)
        ? prev.allowed_tiers.filter(t => t !== tier)
        : [...prev.allowed_tiers, tier],
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.slug || !formData.purpose) {
      toast.error('Name, slug, and purpose are required');
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
      toast.success(isNew ? 'Method created successfully' : 'Method updated successfully');
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl bg-stone-900 border-stone-800 text-white">
      <DialogHeader>
        <DialogTitle>{isNew ? 'Create Method' : 'Edit Method'}</DialogTitle>
        <DialogDescription className="text-slate-400">
          Define a processing method for dispute handling.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 max-h-[60vh] overflow-y-auto py-4">
        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Method Name *</label>
            <Input
              value={formData.name}
              onChange={handleNameChange}
              placeholder="e.g., Balance Dispute Handler"
              className="bg-stone-800 border-slate-700 text-white"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Slug *</label>
            <Input
              value={formData.slug}
              onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
              placeholder="balance_dispute_handler"
              className="bg-stone-800 border-slate-700 text-white font-mono text-sm"
            />
          </div>
        </div>

        <div>
          <label className="text-sm text-slate-400 mb-1 block">Purpose *</label>
          <Textarea
            value={formData.purpose}
            onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
            placeholder="Describe what this method does..."
            className="bg-stone-800 border-slate-700 text-white resize-none"
            rows={2}
          />
        </div>

        <div>
          <label className="text-sm text-slate-400 mb-1 block">Description</label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Additional details..."
            className="bg-stone-800 border-slate-700 text-white resize-none"
            rows={2}
          />
        </div>

        {/* Trigger & Status */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Trigger Type</label>
            <Select
              value={formData.trigger_type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, trigger_type: value }))}
            >
              <SelectTrigger className="bg-stone-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-stone-800 border-slate-700">
                {TRIGGER_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Execution Order</label>
            <Input
              type="number"
              value={formData.execution_order}
              onChange={(e) => setFormData(prev => ({ ...prev, execution_order: parseInt(e.target.value) || 100 }))}
              className="bg-stone-800 border-slate-700 text-white"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Status</label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
            >
              <SelectTrigger className="bg-stone-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-stone-800 border-slate-700">
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Allowed Tiers */}
        <div>
          <label className="text-sm text-slate-400 mb-2 block">Allowed Tiers</label>
          <div className="flex gap-2">
            {['auto_starter', 'specialist_pro', 'agency'].map(tier => (
              <button
                key={tier}
                onClick={() => toggleTier(tier)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                  formData.allowed_tiers.includes(tier)
                    ? 'bg-amber-600 text-white'
                    : 'bg-stone-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                {tier.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Linked Items */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Linked Prompt ID</label>
            <Input
              value={formData.linked_prompt_id}
              onChange={(e) => setFormData(prev => ({ ...prev, linked_prompt_id: e.target.value }))}
              placeholder="prompt_xxxxx"
              className="bg-stone-800 border-slate-700 text-white font-mono text-sm"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Linked Template ID</label>
            <Input
              value={formData.linked_template_id}
              onChange={(e) => setFormData(prev => ({ ...prev, linked_template_id: e.target.value }))}
              placeholder="tmpl_xxxxx"
              className="bg-stone-800 border-slate-700 text-white font-mono text-sm"
            />
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
          {isNew ? 'Create Method' : 'Save Changes'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

const MethodLibraryPage = () => {
  const { getAuthHeaders } = useAuth();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [triggerFilter, setTriggerFilter] = useState('all');
  const [editingMethod, setEditingMethod] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchMethods = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (triggerFilter !== 'all') params.append('method_type', triggerFilter);
      
      const response = await axios.get(
        `${API}/rules-methods/methods${params.toString() ? `?${params.toString()}` : ''}`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setMethods(response.data.methods || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load methods');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, statusFilter, triggerFilter]);

  useEffect(() => {
    fetchMethods();
  }, [fetchMethods]);

  const handleSave = async (formData) => {
    try {
      if (editingMethod) {
        await axios.put(
          `${API}/rules-methods/methods/${editingMethod.id}`,
          formData,
          { headers: getAuthHeaders(), withCredentials: true }
        );
        toast.success('Method updated');
      } else {
        await axios.post(
          `${API}/rules-methods/methods`,
          formData,
          { headers: getAuthHeaders(), withCredentials: true }
        );
        toast.success('Method created');
      }
      fetchMethods();
      setEditingMethod(null);
      setIsCreating(false);
    } catch (error) {
      toast.error('Failed to save method');
      console.error(error);
    }
  };

  const handleDelete = async (method) => {
    if (!window.confirm(`Are you sure you want to archive "${method.name}"?`)) return;
    try {
      await axios.put(
        `${API}/rules-methods/methods/${method.id}`,
        { status: 'archived' },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Method archived');
      fetchMethods();
    } catch (error) {
      toast.error('Failed to archive method');
    }
  };

  const filteredMethods = methods.filter(method =>
    method.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    method.slug?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    method.purpose?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="method-library-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Library className="text-emerald-400" size={28} />
            Method Library
          </h1>
          <p className="text-slate-500 mt-1">
            Define processing methods for dispute analysis and generation
          </p>
        </div>
        <Button onClick={() => { setEditingMethod(null); setIsCreating(true); }}>
          <Plus size={18} className="mr-2" />
          New Method
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search methods..."
            className="pl-10 bg-stone-900 border-stone-800 text-white placeholder:text-slate-500"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 bg-stone-900 border-stone-800 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-stone-800 border-slate-700">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
          </SelectContent>
        </Select>
        <Select value={triggerFilter} onValueChange={setTriggerFilter}>
          <SelectTrigger className="w-40 bg-stone-900 border-stone-800 text-white">
            <SelectValue placeholder="Trigger" />
          </SelectTrigger>
          <SelectContent className="bg-stone-800 border-slate-700">
            <SelectItem value="all">All Triggers</SelectItem>
            {TRIGGER_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={fetchMethods}>
          <RefreshCw size={18} />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Total Methods</p>
          <p className="text-2xl font-bold text-white">{total}</p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Active</p>
          <p className="text-2xl font-bold text-emerald-400">
            {methods.filter(m => m.status === 'active').length}
          </p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Auto-Triggered</p>
          <p className="text-2xl font-bold text-amber-400">
            {methods.filter(m => m.trigger_type !== 'manual').length}
          </p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Manual</p>
          <p className="text-2xl font-bold text-slate-400">
            {methods.filter(m => m.trigger_type === 'manual').length}
          </p>
        </div>
      </div>

      {/* Methods Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-56 bg-stone-800" />)}
        </div>
      ) : filteredMethods.length === 0 ? (
        <div className="text-center py-16 bg-stone-900 rounded-xl border border-stone-800">
          <Library className="mx-auto text-slate-600 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-white mb-2">No Methods Found</h3>
          <p className="text-slate-500 mb-4">
            {searchQuery ? 'No methods match your search.' : 'Create your first method to get started.'}
          </p>
          <Button onClick={() => { setEditingMethod(null); setIsCreating(true); }}>
            <Plus size={18} className="mr-2" />
            Create Method
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMethods.map(method => (
            <MethodCard
              key={method.id}
              method={method}
              onEdit={setEditingMethod}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={isCreating || !!editingMethod} onOpenChange={(open) => { if (!open) { setIsCreating(false); setEditingMethod(null); } }}>
        <MethodEditor
          method={editingMethod}
          onSave={handleSave}
          onClose={() => { setIsCreating(false); setEditingMethod(null); }}
          isNew={isCreating && !editingMethod}
        />
      </Dialog>
    </div>
  );
};

export default MethodLibraryPage;
